import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { QueryParams } from "@router/route-params";
import { runtimeConfig } from "@src/runtime-config";
import { KeyCombination, keyCombinationsMap } from "@loaders/key-combinations";
import { RuntimeUtils } from "@stellar/utils";
import { ReactChildrenOnly } from "@utils/utility-types";

/**
 * Component that listens to key events to trigger certain actions.
 */
export function KeyBindingsLoader({
  children,
}: ReactChildrenOnly): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const isProduction = useMemo(
    () => RuntimeUtils.isProductionEnvironment(runtimeConfig.appEnv),
    []
  );

  // Sets a query parameter in the URL, without modification of the existing ones
  const setQueryParams = useCallback(
    (updates: Partial<Record<QueryParams, string>>) => {
      const searchParams = new URLSearchParams(location.search);

      // Update all parameters
      for (const [key, value] of Object.entries(updates)) {
        searchParams.set(key, value);
      }

      // Navigate to updated URL
      navigate({
        search: searchParams.toString(),
      });
    },
    [location.search, navigate]
  );

  /** Checks if a certain key combination was pressed */
  const matchKeyCombination = useCallback(
    (event: KeyboardEvent): KeyCombination | undefined => {
      const key = event.code;
      const combinationKey = event.altKey ? `Alt+${key}` : key;

      const combination = keyCombinationsMap.get(combinationKey);

      // If the combination should skip prod and we are not in production, skip
      if (combination?.shouldSkipProd && isProduction) {
        return undefined;
      }

      // If the combination has alt key and the event does not have it, skip
      if (combination?.hasAltKey && !event.altKey) {
        return undefined;
      }

      return combination;
    },
    [isProduction]
  );

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent): void {
      if (!event.altKey) {
        return;
      }

      const combination = matchKeyCombination(event);

      if (combination) {
        const updates: Partial<Record<QueryParams, string>> = {};
        combination.action((key, value) => {
          updates[key] = value;
        });

        setQueryParams(updates);
      }
    }

    // eslint-disable-next-line unicorn/prefer-global-this
    window.addEventListener("keydown", handleKeydown);

    // Cleanup event listener on component unmount
    return () => {
      // eslint-disable-next-line unicorn/prefer-global-this
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [matchKeyCombination, setQueryParams]);

  // eslint-disable-next-line react/jsx-no-useless-fragment -- Needed for returning React.JSX.Element
  return <>{children}</>;
}
